import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { AlertCircle, CheckCircle, CreditCard, ExternalLink, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../../../contexts/hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface TrainerProfile {
  id: string;
  user_id: string;
  stripe_account_id: string | null;
  stripe_charges_enabled: boolean;
  stripe_details_submitted: boolean;
  stripe_onboarding_completed: boolean;
  headline: string | null;
  bio: string | null;
  hourly_rate: number | null;
  languages: string[] | null;
  created_at: string;
  updated_at: string;
}

interface StripeAccountStatus {
  charges_enabled: boolean;
  details_submitted: boolean;
  onboarding_completed: boolean;
  requirements?: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
  };
  country?: string;
  default_currency?: string;
}

export const StripeConnectCard: React.FC = () => {
  const { user } = useAuth();
  const [isTrainer, setIsTrainer] = useState(false);
  const [trainerProfile, setTrainerProfile] = useState<TrainerProfile | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkTrainerStatus();
  }, [user]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const onboardingComplete = urlParams.get('stripe_onboarding_complete');
    
    if (onboardingComplete === 'true') {
      // Refresh status after successful onboarding
      setTimeout(() => {
        if (trainerProfile?.stripe_account_id) {
          fetchStripeStatus(trainerProfile.stripe_account_id);
        }
      }, 2000);
    }
  }, [trainerProfile]);

  const checkTrainerStatus = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get user profile from public.users table
      const { data: userProfile, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;

      // Check if user is a trainer using the users.id
      const { data: trainer, error: trainerError } = await supabase
        .from('trainer_profiles')
        .select('*')
        .eq('user_id', userProfile.id)
        .maybeSingle();

      if (trainerError && trainerError.code !== 'PGRST116') {
        throw trainerError;
      }

      setIsTrainer(!!trainer);
      setTrainerProfile(trainer);

      if (trainer?.stripe_account_id) {
        await fetchStripeStatus(trainer.stripe_account_id);
      }
    } catch (err: any) {
      console.error('Error checking trainer status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async (stripeAccountId: string) => {
    try {
      // Get user profile to get the users.id
      const { data: userProfile, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user!.id)
        .single();

      if (userError) throw userError;

      const response = await fetch('/.netlify/functions/get-stripe-account-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          stripeAccountId, 
          userId: userProfile.id 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Stripe status');
      }

      const status = await response.json();
      setStripeStatus(status);
    } catch (err: any) {
      console.error('Error fetching Stripe status:', err);
      setError(err.message);
    }
  };

  const handleConnectStripe = async () => {
    if (!user) return;

    try {
      setConnecting(true);
      setError(null);

      // Get user profile and email
      const { data: userProfile, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;

      // Get email from auth.users
      const { data: authUser, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      const response = await fetch('/.netlify/functions/create-stripe-account-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: userProfile.id, 
          email: authUser.user.email 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create Stripe account link');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error('Error connecting Stripe:', err);
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const getStatusInfo = () => {
    if (!stripeStatus) {
      return {
        icon: AlertCircle,
        text: 'Not connected',
        color: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-50 dark:bg-amber-900/20',
        borderColor: 'border-amber-200 dark:border-amber-800'
      };
    }

    if (stripeStatus.onboarding_completed) {
      return {
        icon: CheckCircle,
        text: 'Connected and ready',
        color: 'text-emerald-600 dark:text-emerald-400',
        bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
        borderColor: 'border-emerald-200 dark:border-emerald-800'
      };
    }

    if (stripeStatus.details_submitted) {
      return {
        icon: AlertCircle,
        text: 'Pending verification',
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800'
      };
    }

    return {
      icon: AlertCircle,
      text: 'Setup incomplete',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-200 dark:border-amber-800'
    };
  };

  if (loading) {
    return (
      <Card className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Setup</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading payment configuration...</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      </Card>
    );
  }

  if (!isTrainer) {
    return (
      <Card className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Setup</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Available for trainers only</p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Payment processing is only available for trainer accounts. If you're a trainer, please contact support to enable this feature.
          </p>
        </div>
      </Card>
    );
  }

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Card className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Setup</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Connect your Stripe account to receive payments from clients
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className={`p-4 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor} mb-6`}>
        <div className="flex items-center space-x-3">
          <div className="p-1.5 bg-white dark:bg-gray-800 rounded-full shadow-sm">
            <StatusIcon className={`w-4 h-4 ${statusInfo.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <Shield className={`w-4 h-4 ${statusInfo.color}`} />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Payment Processing</span>
            </div>
            <p className={`text-sm font-medium ${statusInfo.color} mt-1`}>
              Status: {statusInfo.text}
            </p>
          </div>
        </div>
      </div>

      {stripeStatus && (
        <div className="space-y-3 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Charges Enabled
                </span>
                {stripeStatus.charges_enabled ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {stripeStatus.charges_enabled ? 'Yes' : 'No'}
              </p>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Details Submitted
                </span>
                {stripeStatus.details_submitted ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {stripeStatus.details_submitted ? 'Yes' : 'No'}
              </p>
            </div>
          </div>

          {stripeStatus.country && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Account Details
                </span>
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Country:</span> {stripeStatus.country?.toUpperCase()}
                </p>
                {stripeStatus.default_currency && (
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">Currency:</span> {stripeStatus.default_currency?.toUpperCase()}
                  </p>
                )}
              </div>
            </div>
          )}

          {stripeStatus.requirements && stripeStatus.requirements.currently_due.length > 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Action Required
                </span>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Complete your Stripe onboarding to start receiving payments
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleConnectStripe}
          disabled={connecting}
          className="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white border-0"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4 mr-2" />
              {stripeStatus?.onboarding_completed ? 'Manage Account' : 'Connect Stripe'}
            </>
          )}
        </Button>

        {stripeStatus?.onboarding_completed && (
          <Button
            variant="outline"
            onClick={() => window.open('https://dashboard.stripe.com/express', '_blank')}
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Stripe Dashboard
          </Button>
        )}
      </div>

      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start space-x-2">
          <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
              Secure Payment Processing
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              Your financial information is securely handled by Stripe, a PCI-compliant payment processor trusted by millions of businesses worldwide.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};